import { Content } from "@pathwright/ui/src/components/content/Content"
import {
  BASE_FILESTACK_OPTIONS,
  MEDIA_TYPES,
  MEDIA_TYPE_MAP
} from "@pathwright/ui/src/components/filestack/constants"
import { pick } from "@pathwright/ui/src/components/filestack/utils"
import markdown from "markdown-it"
import { useCallback, useMemo } from "react"
import SimpleMDE from "react-simplemde-editor"
import styled from "styled-components"
import "./MarkdownEditor.css"
import "./code-style.css"
import "./edit.css"

import hljs from "highlight.js/lib/core"
import bash from "highlight.js/lib/languages/bash"
import csharp from "highlight.js/lib/languages/csharp"
import css from "highlight.js/lib/languages/css"
import java from "highlight.js/lib/languages/java"
import javascript from "highlight.js/lib/languages/javascript"
import plaintext from "highlight.js/lib/languages/plaintext"
import python from "highlight.js/lib/languages/python"
import ruby from "highlight.js/lib/languages/ruby"
import sql from "highlight.js/lib/languages/sql"
import typescript from "highlight.js/lib/languages/typescript"
import html from "highlight.js/lib/languages/xml"
import { useEffect } from "react"

hljs.registerLanguage("javascript", javascript)
hljs.registerLanguage("plaintext", plaintext)
hljs.registerLanguage("python", python)
hljs.registerLanguage("html", html)
hljs.registerLanguage("css", css)
hljs.registerLanguage("bash", bash)
hljs.registerLanguage("ruby", ruby)
hljs.registerLanguage("typescript", typescript)
hljs.registerLanguage("java", java)
hljs.registerLanguage("csharp", csharp)
hljs.registerLanguage("sql", sql)

const MDEditText = styled.div`
  font-family: "Operator Mono", "SFMono-Regular", Consolas, "Liberation Mono",
    Menlo, Courier, monospace;
  line-height: 1.45;

  ${(p) =>
    p.cursorColor
      ? `
    .CodeMirror-cursor {
      border-color: ${p.cursorColor};
    }
  `
      : ""}
`

const options = {
  indentWithTabs: true,
  tabSize: 2,
  toolbar: false,
  autoFocus: false,
  spellChecker: false,
  status: false,
  lineNumbers: false,
  lineWrapping: true
}

const MarkdownEdit = ({ data, onChange, blockStyles }) => {
  const md = useMemo(() => {
    return markdown({
      html: true,
      linkify: true,
      typographer: true,
      highlight: (code, lang) => {
        if (lang === "text") lang = "plaintext"
        if (lang === "html") lang = "xml"
        const result =
          lang && hljs.getLanguage(lang)
            ? hljs.highlight(code, { language: lang })
            : hljs.highlightAuto(code)
        return `<pre class="hljs code-block language-${
          result.language || "plaintext"
        }" lang="${result.language || "plaintext"}"><code>${
          result.value
        }</code></pre>`
      }
    })
  }, [])

  const handleChange = useCallback((markdown) => {
    onChange({ markdown, html: md.render(markdown) })
  }, [])

  useEffect(() => {
    const to = setTimeout(() => {
      window.dispatchEvent(new Event("resize"))
    }, 200)
    return () => {
      clearTimeout(to)
    }
  }, [])

  const handleUploadImage = async (cm) => {
    const result = await pick({
      ...BASE_FILESTACK_OPTIONS,
      accept: MEDIA_TYPE_MAP[MEDIA_TYPES.IMAGE],
      maxFiles: 1
    })
    const imageURL = result.filesUploaded[0].url
    cm.replaceSelection(`![](${imageURL})`)
    handleChange(cm.getValue())
  }

  const markdownValue = useMemo(() => {
    return data.markdown
  }, [data])

  return (
    <Content.Container>
      <MDEditText
        cursorColor={blockStyles ? blockStyles.styleAttributes.color : ""}
      >
        <SimpleMDE
          autoFocus={false}
          onChange={handleChange}
          value={markdownValue}
          extraKeys={{ "Ctrl-I": handleUploadImage }}
          options={options}
        />
      </MDEditText>
    </Content.Container>
  )
}

export default MarkdownEdit
